/* eslint-disable max-lines */
import { createRef } from 'react';
import { monthNames } from './constants.js';
import { prependStringWithPlaceHolders, deconstructValuesFromDateString, isIncompleteString } from './stringHelpers.js';
import { convertToPositiveNumberIfPossible } from './numberHelpers.js';
import type { DSControlledDateTimePickerT } from '../react-desc-prop-types.js';
import type { DSControlledDateTimePickerInternalsT } from '../sharedTypes.js';

export const getDateValuesFromDate = (day: Date): DateValuesT => ({
  month: day?.getMonth?.() as DSControlledDateTimePickerInternalsT.DateMonth,
  day: day?.getDate?.() as DSControlledDateTimePickerInternalsT.DateDay,
  year: day?.getFullYear?.(),
});
export const getDateStringFromDay = (day: Date): string => {
  const monthFromDay = prependStringWithPlaceHolders(`${day.getMonth() + 1}`, 2); // months from 1-12
  const dayFromDay = prependStringWithPlaceHolders(`${day.getDate()}`, 2);
  const yearFromDay = prependStringWithPlaceHolders(`${day.getFullYear()}`, 4);

  return `${monthFromDay}/${dayFromDay}/${yearFromDay}`;
};

export const getMonthStringFromDay = (day: Date): string => {
  const monthFromDay = prependStringWithPlaceHolders(`${day.getMonth() + 1}`, 2); // months from 1-12
  const yearFromDay = prependStringWithPlaceHolders(`${day.getFullYear()}`, 4);

  return `${monthFromDay}/__/${yearFromDay}`;
};

export const getTimeStringFromDay = (day: Date): string => {
  const hoursNum = day.getHours();
  const meridiem = hoursNum > 12 ? 'PM' : 'AM';
  const twelveHourFormat = hoursNum > 12 ? hoursNum - 12 : hoursNum;
  const minutesNum = day.getMinutes();

  const hoursFromDay = prependStringWithPlaceHolders(`${twelveHourFormat}`, 2);
  const minutesFromDay = prependStringWithPlaceHolders(`${minutesNum}`, 2);

  return `${hoursFromDay}:${minutesFromDay} ${meridiem}`;
};
export const getMonthNameByMonthNumber = (monthNumber: number): string => monthNames[monthNumber];
export const isSameDateMoment = (dateA: Date, dateB: Date): boolean => dateA?.getTime?.() === dateB?.getTime?.();

export const compareTwoDatesDayEquality = (dayA: Date, dayB: Date): boolean => {
  const { day: currD, month: currM, year: currY } = getDateValuesFromDate(dayA);
  const { day: otherD, month: otherM, year: otherY } = getDateValuesFromDate(dayB);
  return currD === otherD && currM === otherM && currY === otherY;
};
export const compareTwoDateAreInSameMonth = (dayA: Date, dayB: Date): boolean => {
  const { month: currM, year: currY } = getDateValuesFromDate(dayA);
  const { month: otherM, year: otherY } = getDateValuesFromDate(dayB);
  return currM === otherM && currY === otherY;
};
export const isSecondDateInPreviousMonth = (dayA: Date, dayB: Date): boolean => {
  const { month: currM, year: currY } = getDateValuesFromDate(dayA);
  const { month: otherM, year: otherY } = getDateValuesFromDate(dayB);
  return (
    (currM - 1 === otherM && currY === otherY) || // anything but January
    // January - currM is 0 so -1 becomes 11, the year is the previous year and the other day month is 11 (December)
    (currM - 1 === -1 && currY - 1 === otherY && otherM === 11)
  );
};
export const isSecondDateInNextMonth = (dayA: Date, dayB: Date): boolean => {
  const { month: currM, year: currY } = getDateValuesFromDate(dayA);
  const { month: otherM, year: otherY } = getDateValuesFromDate(dayB);
  return (
    (currM + 1 === otherM && currY === otherY) || // anything but December
    // December - currM is 11 so +1 becomes 12, the year is the next year and the other day month is 0 (January)
    (currM + 1 === 12 && currY + 1 === otherY && otherM === 0)
  );
};

export const getLastDayOfMonth = (month: DSControlledDateTimePickerInternalsT.DateMonth, year: number): Date =>
  new Date(year, month + 1, 0);
export const isValidDayForTheMonth = (
  month: DSControlledDateTimePickerInternalsT.DateMonth,
  year: number,
  day: DSControlledDateTimePickerInternalsT.DateDay,
): boolean => new Date(year, month, day)?.getMonth?.() === month;
export const getDayFromDateString = (dateString: string): Date | null => {
  const { month, day, year } = deconstructValuesFromDateString(dateString);
  const monthNum = convertToPositiveNumberIfPossible(month);
  const dayNum = convertToPositiveNumberIfPossible(day) as DSControlledDateTimePickerInternalsT.DateDay | -1;
  const yearNum = convertToPositiveNumberIfPossible(year);
  if (
    monthNum !== -1 &&
    dayNum !== -1 &&
    yearNum !== -1 &&
    isValidDayForTheMonth((monthNum - 1) as DSControlledDateTimePickerInternalsT.DateMonth, yearNum, dayNum)
  )
    return new Date(yearNum, monthNum - 1, dayNum);
  return null;
};
export const isValidDayForTheMonthDateString = (dateString: string): boolean => {
  if (isIncompleteString(dateString)) return true;
  const { month } = deconstructValuesFromDateString(dateString);
  const monthInt = convertToPositiveNumberIfPossible(month);
  if (monthInt === -1) return false;
  const monthDateMonth = (monthInt - 1) as DSControlledDateTimePickerInternalsT.DateMonth;
  return getDayFromDateString(dateString)?.getMonth?.() === monthDateMonth;
};

export const getValidationDateStringMetaInfo = (
  dateString: string,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars, no-unused-vars
  getIsOutOfRangeDay = (day: string) => false,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars, no-unused-vars
  getIsDisabledDay = (day: string) => false,
): Partial<DSControlledDateTimePickerT.InternalInputsChangeMetaInfo> => {
  const metaInfo: Partial<DSControlledDateTimePickerT.InternalInputsChangeMetaInfo> = {};
  if (!isIncompleteString(dateString)) {
    metaInfo.isDayPresentInMonth = isValidDayForTheMonthDateString(dateString);
    if (!metaInfo.isDayPresentInMonth)
      metaInfo.suggestedErrorMsg = 'The selected day is not part of the selected month';
    const day = getDayFromDateString(dateString);
    if (day) {
      metaInfo.isOutOfRangeDay = getIsOutOfRangeDay(dateString);
      metaInfo.isDisabledDay = getIsDisabledDay(dateString);
      if (metaInfo.isOutOfRangeDay || metaInfo.isDisabledDay)
        metaInfo.suggestedErrorMsg = 'The selected day is not allowed';
    }
  }
  return metaInfo;
};
// code adapted from https://github.com/datejs/Datejs/blob/master/src/core.js
export const isLeapYear = (year: number): boolean => (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;

export const getHowManyDaysInMonth = (year: number, month: number): number => {
  const daysPerMonth = [31, isLeapYear(year) ? 29 : 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
  return daysPerMonth[month];
};

export const dateIsLeapYear = (date: Date): boolean => isLeapYear(date.getFullYear());

export const dateGetDaysInMonth = (date: Date): number => getHowManyDaysInMonth(date.getFullYear(), date.getMonth());

export const dateAddMonths = (date: Date, value: number): Date => {
  const copiedDate = new Date(date.getTime());
  const n = copiedDate.getDate();
  copiedDate.setDate(1);
  copiedDate.setMonth(copiedDate.getMonth() + value);
  copiedDate.setDate(Math.min(n, dateGetDaysInMonth(copiedDate)));
  return copiedDate;
};
export const dateAddYear = (date: Date, value: number): Date => dateAddMonths(date, value * 12);

interface DateValuesT {
  month: DSControlledDateTimePickerInternalsT.DateMonth;
  year: number;
  day?: DSControlledDateTimePickerInternalsT.DateDay;
}
interface DateSumValuesT {
  monthsToSum?: number;
  yearsToSum?: number;
  daysToSum?: number;
}
// sumFromDateValues supports negative values to "subtract"
export const sumFromDateValues = (
  { month, year, day = 1 }: DateValuesT,
  { monthsToSum = 0, yearsToSum = 0, daysToSum = 0 }: DateSumValuesT,
): DateValuesT => {
  let newDate = new Date(year, month, day);
  if (yearsToSum) newDate = dateAddYear(newDate, yearsToSum);

  if (monthsToSum) newDate = dateAddMonths(newDate, monthsToSum);

  if (daysToSum) newDate.setDate(newDate.getDate() + daysToSum);

  return {
    month: newDate.getMonth() as DSControlledDateTimePickerInternalsT.DateMonth,
    day: newDate.getDate() as DSControlledDateTimePickerInternalsT.DateDay,
    year: newDate.getFullYear(),
  };
};
export const sumValuesToDate = (day: Date, sumValues: DateSumValuesT): Date => {
  const dateAsVals = getDateValuesFromDate(day);
  const { month, year, day: newD } = sumFromDateValues(dateAsVals, sumValues);
  return new Date(year, month, newD);
};
export const subtractYears = (date: Date, howManyMore = 1): Date => {
  const newDate = new Date(date.getTime());
  newDate.setFullYear(date.getFullYear() - howManyMore);
  return newDate;
};
export const subtractMonths = (date: Date, howManyMore = 1): Date => {
  const newDate = new Date(date.getTime());
  newDate.setMonth(date.getMonth() - howManyMore);
  return newDate;
};
export const sumMonths = (date: Date, howManyMore = 1): Date => {
  const newDate = new Date(date.getTime());
  newDate.setMonth(date.getMonth() + howManyMore);
  return newDate;
};
export const sumYears = (date: Date, howManyMore = 1): Date => {
  const newDate = new Date(date.getTime());
  newDate.setFullYear(date.getFullYear() + howManyMore);
  return newDate;
};

export const getDaysInMonth = (month: number, year: number): Date[] => {
  const date = new Date(year, month, 1);
  const days: Date[] = [];
  while (date.getMonth() === month) {
    days.push(new Date(date));
    date.setDate(date.getDate() + 1);
  }
  return days;
};
export const getLastXDaysFromPreviousMonth = (month: number, year: number, howMany: number): Date[] => {
  const date = new Date(year, month, 1);
  const days: Date[] = [];
  while (days.length !== howMany) {
    date.setDate(date.getDate() - 1);
    days.push(new Date(date));
  }
  return days.reverse();
};
export const getFirstXDaysFromNextMonth = (month: number, year: number, howMany: number): Date[] => {
  const date = sumMonths(new Date(year, month, 1), 1);
  const days: Date[] = [];
  while (days.length !== howMany) {
    days.push(new Date(date));
    date.setDate(date.getDate() + 1);
  }
  return days;
};

export const getSixBySevenCurrentMonthDaysMatrix = (
  month: number,
  year: number,
): DSControlledDateTimePickerT.MetaMonthDay[] => {
  const currentMonthDays = getDaysInMonth(month, year).map((day) => ({
    day,
    dayAsString: getDateStringFromDay(day),
    isPrevMonthDay: false,
    isNextMonthDay: false,
    isCurrMonthDay: true,
    isHidden: false,
    dayBtnRef: createRef<HTMLButtonElement>() as React.MutableRefObject<HTMLButtonElement>,
  }));
  const firstDayOfTheMonthWeekDay = currentMonthDays?.[0]?.day?.getDay();
  const previousMonthDaysToPrepend = getLastXDaysFromPreviousMonth(month, year, firstDayOfTheMonthWeekDay).map(
    (day) => ({
      day,
      dayAsString: getDateStringFromDay(day),
      isPrevMonthDay: true,
      isNextMonthDay: false,
      isCurrMonthDay: false,
      isHidden: false,
      dayBtnRef: createRef<HTMLButtonElement>() as React.MutableRefObject<HTMLButtonElement>,
    }),
  );
  const lastDayOfTheMonthWeekDay = currentMonthDays?.[currentMonthDays.length - 1]?.day?.getDay();
  const nextMonthDaysToAppend = getFirstXDaysFromNextMonth(month, year, 6 - lastDayOfTheMonthWeekDay).map((day) => ({
    day,
    dayAsString: getDateStringFromDay(day),
    isPrevMonthDay: false,
    isNextMonthDay: true,
    isCurrMonthDay: false,
    isHidden: false,
    dayBtnRef: createRef<HTMLButtonElement>() as React.MutableRefObject<HTMLButtonElement>,
  }));

  const allDays = [...previousMonthDaysToPrepend, ...currentMonthDays, ...nextMonthDaysToAppend];

  // we want to be sure that we have 6 rows and 7 columns always (worst case scenario - this ensure no jump of popperjs when changing months)
  const is6by7 = allDays.length === 42;
  if (is6by7) return allDays;

  // by default we prepend an extra week of previous month days
  const daysBefore = 7 + firstDayOfTheMonthWeekDay;
  // and we append just enough days to fill the 6th row of the current month
  let daysAfter = 6 - lastDayOfTheMonthWeekDay;
  // in some extreme cases we might have 4 rows and 7 columns (e.g. February 2026)
  // we can check it by checking that previousMonthDaysToPrepend length is 0 + currentMonthDays length is 28 + nextMonthDaysToAppend length is 0
  const is4by7 =
    previousMonthDaysToPrepend.length === 0 && currentMonthDays.length === 28 && nextMonthDaysToAppend.length === 0;
  if (is4by7) {
    // in this case we append an extra week of next month days
    daysAfter = 7;
  }

  // we add a "previous month" rows at the start of the array to make sure we have 6 rows
  const previousMonthDaysToPrepend2 = getLastXDaysFromPreviousMonth(month, year, daysBefore).map((day) => ({
    day,
    dayAsString: getDateStringFromDay(day),
    isPrevMonthDay: true,
    isNextMonthDay: false,
    isCurrMonthDay: false,
    isHidden: false,
    dayBtnRef: createRef<HTMLButtonElement>() as React.MutableRefObject<HTMLButtonElement>,
  }));
  const nextMonthDaysToAppend2 = getFirstXDaysFromNextMonth(month, year, daysAfter).map((day) => ({
    day,
    dayAsString: getDateStringFromDay(day),
    isPrevMonthDay: false,
    isNextMonthDay: true,
    isCurrMonthDay: false,
    isHidden: false,
    dayBtnRef: createRef<HTMLButtonElement>() as React.MutableRefObject<HTMLButtonElement>,
  }));

  return [...previousMonthDaysToPrepend2, ...currentMonthDays, ...nextMonthDaysToAppend2];
};
